import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Calendar, BookOpen, Star, ChevronRight, Users } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useCart } from '../contexts/CartContext';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import FeaturedCarousel from '../components/FeaturedCarousel';
import RecommendedForYou from '../components/RecommendedForYou';
import { getLocalizedField } from '../utils/localizedContent';


const HERO_IMAGES = {
  dark:  'https://images.pexels.com/photos/7689135/pexels-photo-7689135.jpeg?auto=compress&cs=tinysrgb&w=1600&h=900&fit=crop',
  light: 'https://images.pexels.com/photos/7689135/pexels-photo-7689135.jpeg?auto=compress&cs=tinysrgb&w=1600&h=900&fit=crop',
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const { addItem } = useCart();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products?limit=6`).then(r => setProducts(r.data.products || [])).catch(() => {});
    axios.get(`${API}/events?limit=3`).then(r => setEvents(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const features = [
    { key: 'store', icon: ShoppingBag, title: t('home.feature_store'), desc: t('home.feature_store_desc'), link: '/store', color: '#C9A962' },
    { key: 'reserve', icon: BookOpen, title: t('home.feature_reserve'), desc: t('home.feature_reserve_desc'), link: '/reservations/games', color: '#2D8B70' },
    { key: 'table', icon: Calendar, title: t('home.feature_table'), desc: t('home.feature_table_desc'), link: '/reservations/tables', color: '#0D5E4A' },
    { key: 'events', icon: null, title: t('home.feature_events'), desc: t('home.feature_events_desc'), link: '/torneos', color: '#D4A84B' },
  ];

  const eventTypeLabel = (type) => {
    const labels = {
      tournament: t('events.type_tournament'),
      casual: t('events.type_casual'),
      draft: t('events.type_draft'),
      sealed: t('events.type_sealed'),
    };
    return labels[type] || type;
  };

  return (
    <div className="page-enter">
      {/* Hero — image changes with theme */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGES[theme] || HERO_IMAGES.dark}
            alt="Hero background"
            className="w-full h-full object-cover"
            style={{
              filter: theme === 'light'
                ? 'brightness(1.35) contrast(0.85) saturate(0.8)'
                : 'none'
            }}
          />
          {/* Overlay — lighter gradient in light mode */}
          <div className={`absolute inset-0 ${theme === 'light'
            ? 'bg-gradient-to-r from-white/90 via-white/70 to-white/30'
            : 'bg-gradient-to-r from-[#090F0C]/97 via-[#090F0C]/85 to-[#090F0C]/50'
          }`}></div>
          <div className={`absolute inset-0 ${theme === 'light'
            ? 'bg-gradient-to-t from-white/80 via-transparent to-white/30'
            : 'bg-gradient-to-t from-[#090F0C] via-transparent to-[#090F0C]/30'
          }`}></div>

          {/* Arcade grid overlay — only in dark mode */}
          {theme !== 'light' && (<div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(201,169,98,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(201,169,98,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>)}

          {theme !== 'light' && (<>
          <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-[#C9A962]/30 to-transparent"
               style={{ top: '30%', filter: 'blur(1px)' }}></div>
          <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-[#2D8B70]/20 to-transparent"
               style={{ top: '70%', filter: 'blur(1px)' }}></div>
          </>)}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-xl">
            {/* Arcade-style status tag */}
            <div className="inline-flex items-center gap-2 bg-[#0D5E4A]/25 border border-[#C9A962]/40 rounded-full px-4 py-2 text-sm font-medium mb-6 backdrop-blur-sm"
                 style={{
                   color: theme === 'light' ? '#7A5E1A' : '#C9A962',
                   boxShadow: theme === 'light' ? 'none' : '0 0 12px rgba(201,169,98,0.15)',
                 }}>
              <span className="w-2 h-2 rounded-full bg-[#C9A962] animate-pulse" style={{ boxShadow: '0 0 4px #C9A962' }}></span>
              {t('home.status_tag')}
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-foreground mb-6 leading-tight"
                style={{ fontFamily: 'Cinzel, serif', textShadow: '0 0 40px rgba(201,169,98,0.15)' }}>
              {t('home.hero_title')}
            </h1>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">{t('home.hero_subtitle')}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/store" className="btn-primary text-base px-8 py-4 inline-flex items-center gap-2" data-testid="hero-shop-btn">
                {t('home.hero_cta')} <ChevronRight size={18} />
              </Link>
              <Link to="/reservations/tables" className="btn-secondary text-base px-8 py-4 inline-flex items-center gap-2" data-testid="hero-reserve-btn">
                {t('home.hero_cta2')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        {/* Featured Carousel */}
        <div className="mb-12">
          <FeaturedCarousel />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <Link key={f.key} to={f.link} className="card-glass p-6 flex flex-col gap-4 cursor-pointer group" data-testid={`feature-${f.key}`}>
              {f.icon && (
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${f.color}20`, border: `1px solid ${f.color}40` }}>
                  <f.icon size={22} style={{ color: f.color }} />
                </div>
              )}
              <div>
                <h3 className="text-foreground font-bold text-base mb-1" style={{ fontFamily: 'Cinzel, serif' }}>{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
              </div>
              <div className="flex items-center gap-1 text-xs font-semibold mt-auto" style={{ color: f.color }}>
                {t('home.explore')} <ChevronRight size={14} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Cinzel, serif' }}>{t('home.featured')}</h2>
          <Link to="/store" className="flex items-center gap-1 text-[#C9A962] text-sm font-medium hover:gap-2 transition-all">{t('home.see_all')} <ChevronRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {products.map(product => (
            <div key={product.product_id} className="card-glass overflow-hidden group cursor-pointer" data-testid={`product-card-${product.product_id}`}>
              <Link to={`/store/${product.product_id}`}>
                <div className="aspect-square overflow-hidden">
                  <img src={product.images?.[0] || 'https://images.unsplash.com/photo-1611371805429-8b5c1b2c34ba?w=300&h=300&fit=crop'} alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-3">
                  <p className="text-foreground text-xs font-semibold truncate">{getLocalizedField(product, 'name', lang, product.name)}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Star size={10} className="text-[#C9A962] fill-[#C9A962]" />
                    <span className="text-muted-foreground text-xs">{product.rating?.toFixed(1)}</span>
                  </div>
                  <p className="text-[#C9A962] text-sm font-bold mt-1">{product.price?.toFixed(2)}€</p>
                </div>
              </Link>
              <div className="px-3 pb-3">
                <button onClick={() => addItem(product)} className="w-full text-xs font-semibold py-1.5 rounded-lg bg-[#C9A962]/10 text-[#C9A962] hover:bg-[#C9A962]/20 transition-colors" data-testid={`add-to-cart-${product.product_id}`}>
                  + {t('home.add_to_cart')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Recommended For You */}
      <RecommendedForYou />

      {/* Upcoming Events */}
      {events.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Cinzel, serif' }}>{t('home.events_title')}</h2>
            <Link to="/events" className="flex items-center gap-1 text-[#C9A962] text-sm font-medium">{t('home.see_all')} <ChevronRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {events.map(event => (
              <Link key={event.event_id} to={`/events/${event.event_id}`} className="card-glass overflow-hidden group" data-testid={`event-card-${event.event_id}`}>
                {event.image && (
                  <div className="h-40 overflow-hidden">
                    <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#0D5E4A]/30 text-[#2D8B70]">{eventTypeLabel(event.event_type)}</span>
                    {event.entry_fee === 0 && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#C9A962]/20 text-[#C9A962]">{t('events.free')}</span>}
                  </div>
                  <h3 className="text-foreground font-bold text-sm mb-2" style={{ fontFamily: 'Cinzel, serif' }}>{getLocalizedField(event, 'title', lang, event.title)}</h3>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar size={12} />{event.date}</span>
                    <span className="flex items-center gap-1"><Users size={12} />{event.registered_participants?.length || 0}/{event.max_participants}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      {!user && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="relative rounded-2xl p-10 text-center border border-[#C9A962]/30 overflow-hidden celtic-border">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0D5E4A]/20 via-[#C9A962]/10 to-[#2D8B70]/20"></div>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0iI0M5QTk2MiIgb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')] opacity-50"></div>
            <div className="relative z-10">
              <h2 className="text-4xl font-black text-foreground mb-4" style={{ fontFamily: 'Cinzel, serif' }}>{t('home.join_title')}</h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">{t('home.join_sub')}</p>
              <Link to="/auth" className="btn-primary text-base px-10 py-4 inline-block" data-testid="join-cta-btn">{t('home.join_cta')}</Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
